import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { isNamedLead, leadDisplayLabel } from "@/lib/leadDisplayLabel";

export interface ContactLeadOption {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
}

interface ContactLeadPickerProps {
  value?: string;
  selectedLead?: ContactLeadOption | null;
  onSelect: (lead: ContactLeadOption) => void;
  disabled?: boolean;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function ContactLeadPicker({
  value,
  selectedLead,
  onSelect,
  disabled,
}: ContactLeadPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ContactLeadOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [resolvedLead, setResolvedLead] = useState<ContactLeadOption | null>(
    selectedLead ?? null,
  );
  const debouncedQuery = useDebouncedValue(query, 300);
  const searchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (selectedLead) {
      setResolvedLead(selectedLead);
    }
  }, [selectedLead]);

  useEffect(() => {
    if (!value) {
      setResolvedLead(null);
      return;
    }
    if (selectedLead && selectedLead._id === value) return;
    if (resolvedLead?._id === value) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`/api/leads/${value}`);
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const lead = data.lead as ContactLeadOption | undefined;
        if (lead?._id && !cancelled) {
          setResolvedLead(lead);
        }
      } catch {
        if (!cancelled) setResolvedLead(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, selectedLead, resolvedLead?._id]);

  useEffect(() => {
    if (!open) return;

    searchAbortRef.current?.abort();
    const ac = new AbortController();
    searchAbortRef.current = ac;

    const run = async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({
          dateRange: "All",
          limit: "25",
          page: "1",
          sortBy: "name",
          sortDir: "asc",
        });
        const q = debouncedQuery.trim();
        if (q) params.set("search", q);

        const response = await fetch(`/api/leads?${params.toString()}`, {
          signal: ac.signal,
        });
        if (!response.ok) {
          setResults([]);
          return;
        }
        const data = await response.json();
        const leads = (data.leads || []) as ContactLeadOption[];
        setResults(leads.filter((lead) => isNamedLead(lead)));
      } catch (e) {
        if ((e as Error).name !== "AbortError") {
          setResults([]);
        }
      } finally {
        if (!ac.signal.aborted) setIsSearching(false);
      }
    };

    void run();
    return () => ac.abort();
  }, [open, debouncedQuery]);

  const handleSelect = useCallback(
    (lead: ContactLeadOption) => {
      setResolvedLead(lead);
      onSelect(lead);
      setOpen(false);
      setQuery("");
    },
    [onSelect],
  );

  const triggerLabel =
    value && resolvedLead && isNamedLead(resolvedLead) ?
      leadDisplayLabel(resolvedLead)
    : value ?
      "Loading contact…"
    : "Select contact…";

  return (
    <div className="space-y-2">
      <Label>Contact</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
            disabled={disabled}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-2" align="start">
          <Input
            placeholder="Search name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="mt-2 max-h-60 overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : results.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {debouncedQuery.trim() ? "No contacts found" : "Type to search contacts"}
              </p>
            ) : (
              <ul className="space-y-0.5">
                {results.map((lead) => {
                  const isSelected = value === lead._id;
                  return (
                    <li key={lead._id}>
                      <button
                        type="button"
                        className={cn(
                          "flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
                          isSelected && "bg-accent",
                        )}
                        onClick={() => handleSelect(lead)}
                      >
                        <Check
                          className={cn(
                            "mt-0.5 h-4 w-4 shrink-0",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {leadDisplayLabel(lead)}
                          </span>
                          {(lead.email || lead.phoneNumber) && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {[lead.email, lead.phoneNumber].filter(Boolean).join(" · ")}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
